import type { ReactNode } from 'react';
import { createContext, use } from 'react';
import { useStore } from '@nanostores/react';
import { useLocation } from '@/services/router/compat';
import { accountStore } from '@/services/auth/store';
import { getAppBundleFromUrl } from '../utils/navigation';
import type { AppBundle } from '../utils/app-bundles';

export const ActiveAppContext = createContext<AppBundle | null | undefined>(undefined);

function useActiveBundleInternal() {
  const location = useLocation();
  // The active bundle is a function of the URL and the account's feature flags
  // (flag-gated route ownership). On a hard load this renders before the account
  // exists; subscribing here re-derives the bundle once the account loads, instead
  // of caching the pre-auth answer until the next navigation.
  const account = useStore(accountStore);
  return getAppBundleFromUrl(location.pathname, account);
}

export function useActiveBundle() {
  const context = use(ActiveAppContext);
  if (context === undefined) {
    throw new Error('useActiveBundle must be used within an ActiveAppContext');
  }
  return context;
}

type ActiveAppContextProviderProps = { children: ReactNode };

export function ActiveAppContextProvider({ children }: ActiveAppContextProviderProps) {
  const activeApp = useActiveBundleInternal();

  return <ActiveAppContext.Provider value={activeApp}>{children}</ActiveAppContext.Provider>;
}
